import url from 'url'
import * as R from 'ramda'
import promiseRetry from 'promise-retry'
import '@/assets/tableauwdc-2.3.0.min'
import EventsAPI from '@/app/api/phq-public/events'
import {
  cleanApiParams,
  removeAllDateParams,
  getRangeChunks,
  getEventsPage,
  formatDateFilter,
  OVERFLOW_ERROR_CODE,
  COLUMN_ORDER
} from '@/app/utils'

let _connector, _isRunningInTableau, _isOldTableau

const orderTableauExportColumns = (columns) => {
  const flattenedColumns = columns.map(column => column.id)
  const indexes = COLUMN_ORDER.map(field => flattenedColumns.indexOf(field)).filter(field => field !== -1)
  return indexes.map(index => columns[index])
}

const getAllEventsPages = (params, token, pages) => {
  return R.range(1, pages + 1).map(pageNumber => {
    let newParams = Object.assign({}, params)
    newParams['offset'] = pageNumber * newParams.limit
    return getEventsPage(newParams, token)
  })
}

const getEventsRange = function (params, dateType, previousRangeMeta, ltSuffix, gtSuffix) {
  return new Promise((resolve, reject) => {
    return promiseRetry(retry => {
      return EventsAPI.search(cleanApiParams(params), params.token).then(async function (data) {
        if (params.getAllPages) {
          if (data.overflow) {
            // Dividing range into chunks and creating a promise for each chunk
            let rangeChunks = getRangeChunks(params, dateType, previousRangeMeta, ltSuffix, gtSuffix)
            return Promise.all(rangeChunks.chunks.map(chunk => {
              let newParams = Object.assign({}, removeAllDateParams(params))
              newParams[`${dateType}.${chunk.lt}`] = chunk.end.format('YYYY-MM-DD')
              newParams[`${dateType}.${chunk.gt}`] = chunk.start.format('YYYY-MM-DD')
              newParams.getAllPages = true
              // getAllPages is always false here
              return getEventsRange(newParams, true, dateType, rangeChunks.range, chunk.lt, chunk.gt)
            })).then(dataSet => {
              resolve(dataSet.reduce((acc, events) => {
                acc = acc.concat(events)
                return acc
              }))
            })
          } else {
            // This chunk is within overflow limit so lets just paginate it
            if (data.next) {
              let parsedUrl = url.parse(data.next, true)
              const newParams = Object.assign({}, cleanApiParams(params))
              newParams.limit = parseInt((parsedUrl.query.offset))
              let pages = Math.floor(data.count / newParams.limit)
              return Promise.all(getAllEventsPages(cleanApiParams(newParams), params.token, pages)).then(dataSet => {
                resolve(dataSet.reduce((acc, response) => {
                  acc = acc.concat(response['results'])
                  return acc
                }, data['results']))
              }).catch(error => {
                reject(error)
              })
            } else {
              resolve(data['results'])
            }
          }
        } else {
          resolve(data['results'])
        }
      }).catch(error => {
        if (error.code === OVERFLOW_ERROR_CODE) {
          window.tableau.abortWithError(
            'Sorry, this request is too large.\n' +
            'Please make your filters more specific to reduce the number of results.'
          )
          reject(error)
        } else if (error.code >= 400) {
          error.response.json().then(response => {
            if (response.hasOwnProperty('error')) {
              window.tableau.abortWithError(
                response.error
              )
        } else {
              window.tableau.abortWithError(
                'Oh no! Looks like there is something wrong with your filters.' +
                'Please change them and try again.'
              )
            }
          })
          reject(error)
        } else {
          retry(error)
        }
      })
    })
  })
}

const getSchema = (isOldTableau, hasEntities, hasAviationRank, hasPhqAttendance, schemaCallback) => {
  let rowCols = [
    {
      id: 'id',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'title',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'description',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'start',
      dataType: window.tableau.dataTypeEnum.datetime
    }, {
      id: 'end',
      dataType: window.tableau.dataTypeEnum.datetime
    }, {
      id: 'predicted_end',
      dataType: window.tableau.dataTypeEnum.datetime
    }, {
      id: 'duration',
      dataType: window.tableau.dataTypeEnum.int
    }, {
      id: 'category',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'labels',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'timezone',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'country_code',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'scope',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'phq_rank',
      dataType: window.tableau.dataTypeEnum.int
    }, {
      id: 'local_rank',
      dataType: window.tableau.dataTypeEnum.int
    }, {
      id: 'event_status',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'first_seen',
      dataType: window.tableau.dataTypeEnum.datetime
    }, {
      id: 'updated',
      dataType: window.tableau.dataTypeEnum.datetime
    }
  ]

  if (hasAviationRank) {
    rowCols.push({
      id: 'aviation_rank',
      dataType: window.tableau.dataTypeEnum.int
    })
  }

  if (hasPhqAttendance) {
    rowCols.push({
      id: 'phq_attendance',
      dataType: window.tableau.dataTypeEnum.int
    })
  }

  if (isOldTableau) {
    rowCols = rowCols.concat([
      {
        id: 'lat',
        dataType: window.tableau.dataTypeEnum.string
      }, {
        id: 'lon',
        dataType: window.tableau.dataTypeEnum.string
      }
    ])
  } else {
    rowCols.push({
      id: 'geo_point',
      dataType: window.tableau.dataTypeEnum.geometry
    })
    rowCols.push({
      id: 'geo_polygon',
      dataType: window.tableau.dataTypeEnum.geometry
    })
    rowCols.push({
      id: 'geo_multipolygon',
      dataType: window.tableau.dataTypeEnum.geometry
    })
  }

  if (hasEntities) {
    rowCols = rowCols.concat([{
      id: 'venue_id',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'venue_name',
      dataType: window.tableau.dataTypeEnum.string
    }, {
      id: 'venue_formatted_address',
      dataType: window.tableau.dataTypeEnum.string
    }])
  }

  const rowsTable = {
    id: 'events',
    alias: 'predicthq_events',
    columns: orderTableauExportColumns(rowCols)
  }

  schemaCallback([rowsTable])
}

const getEvents = (params, dateFilter, isOldTableau, hasEntities, hasAviationRank, hasPhqAttendance, table, doneCallback) => {
  // It's really important that a limit is set - there were issues with users misunderstanding the limit option and unsetting it
  if (params["limit"] == undefined) {
    params["limit"] = 500
  }

  const date = formatDateFilter(dateFilter)
  // default range meta
  const rangeMeta = {
    length: 365
  }
  getEventsRange(params, date.type, rangeMeta, date.lt, date.gt).then(allRecords => {
    const processedEvents = {}
    //  filtering events and converting them to tableau row objects
    const tableData = allRecords.reduce((acc, row) => {
      if (!processedEvents[row.id]) {
        processedEvents[row.id] = true
        const dataRow = {
          id: row.id,
          title: row.title,
          description: row.description,
          start: row.start,
          end: row.end,
          predicted_end: row.predicted_end,
          updated: row.updated,
          duration: row.duration,
          labels: row.labels.join(', '),
          category: row.category,
          timezone: row.timezone,
          country_code: row.country,
          scope: row.scope,
          phq_rank: row.rank,
          local_rank: row.local_rank,
          event_status: row.state,  // Tableau assumes anything with "state" means state as in region
          first_seen: row.first_seen
        }

        if (hasAviationRank) {
          dataRow.aviation_rank = row.aviation_rank
        }

        if (hasPhqAttendance) {
          dataRow.phq_attendance = row.phq_attendance
        }

        if (hasEntities && row.entities.length > 0) {
          for (const entity of row.entities) {
            if (entity.type == "venue") {
              dataRow.venue_id = entity.entity_id
              dataRow.venue_name = entity.name
              dataRow.venue_formatted_address = entity.formatted_address ? entity.formatted_address.replace(/\n/gi, ', ') : null
            }
          }
        }
        if (isOldTableau) {
          const splitLocation = row.location
          dataRow.lon = splitLocation[0]
          dataRow.lat = splitLocation[1]
        } else if (row.geo !== undefined) {
          if (row.geo.geometry.type == "Point") {
            dataRow.geo_point = row.geo.geometry
          } else if (row.geo.geometry.type == "Polygon") {
            dataRow.geo_point = {
              type: 'Point',
              coordinates: row.location
            }

            dataRow.geo_polygon = row.geo.geometry
          } else if (row.geo.geometry.type == "MultiPolygon") {
            dataRow.geo_point = {
              type: 'Point',
              coordinates: row.location
            }

            dataRow.geo_multipolygon = row.geo.geometry
          }
        }
        acc.push(dataRow)
      }
      return acc
    }, [])
    
    // Tableau can't handle all the data at one time and needs to be drip fed in.
    var row_index = 0;
    var size = 1000;
    while (row_index < tableData.length){
      table.appendRows(tableData.slice(row_index, size + row_index));
      row_index += size;
      window.tableau.reportProgress("Getting row: " + row_index);
    }

    doneCallback()
  })
}

export const WDCWrapper = {
  init () {
    _connector = window.tableau.makeConnector()
    _connector.getData = function (table, doneCallback) {
      const { params, dateFilter, isOldTableau, hasEntities, hasAviationRank, hasPhqAttendance } = JSON.parse(window.tableau.connectionData)
      getEvents(params, dateFilter, isOldTableau, hasEntities, hasAviationRank, hasPhqAttendance, table, doneCallback)
    }
    _connector.getSchema = (schemaCallback) => {
      const { isOldTableau, hasEntities, hasAviationRank, hasPhqAttendance } = JSON.parse(window.tableau.connectionData)
      getSchema(isOldTableau, hasEntities, hasAviationRank, hasPhqAttendance, schemaCallback)
    }
    window.tableau.registerConnector(_connector)
  },
  submitData (dateFilter, params, hasAviationRank, hasPhqAttendance) {
    EventsAPI.search({ limit: 10 }, params.token).then(events => {
      // Getting a sample of the events so that we can check whether user has access to entities/local_rank
      // TODO update to check for other properties that are added in the future
      let hasEntities = false
      const event = events.results[0]
      if (event.entities) {
        hasEntities = true
      }
      window.tableau.connectionName = 'PredictHQ'
      window.tableau.connectionData = JSON.stringify({
        isOldTableau: this.isOldTableau(),
        dateFilter,
        params,
        hasEntities,
        hasAviationRank,
        hasPhqAttendance
      })
      window.tableau.submit()
    })
  },
  isRunningInTableau () {
    if (_isRunningInTableau === undefined) {
      let userAgent = String(navigator.userAgent)
      // This check will exclude the WebDataConnector Simulator
      _isRunningInTableau = userAgent.indexOf('Tableau') > -1 || window.hasOwnProperty('tableauVersionBootstrap')
    }
    return _isRunningInTableau
  },
  isOldTableau () {
    if (_isOldTableau === undefined) {
      let userAgent = String(navigator.userAgent)
      let filtered = userAgent.split(' ').filter(usElem => usElem.indexOf('Tableau/10') > -1)
      if (filtered.length > 0) {
        let tableauVersion = parseFloat(filtered[0].split('/')[1])
        _isOldTableau = isNaN(tableauVersion) ? true : (tableauVersion < 10.4)
        return _isOldTableau
      }
      _isOldTableau = null
    }
    return _isOldTableau
  }
}
